import { useEffect, useState } from 'react'
import { adminApi } from '../lib/api'
import { transformUsers } from '../lib/transformers'
import { Ban, CheckCircle, X } from 'lucide-react'
import Pagination from '../components/Pagination'
import { SingleFileUploader } from '../components/files/SingleFileUploader'
import type { UploadedFile, User } from '../types'
import { useToast } from '../components/ui/ToastProvider'

export default function UsersPage() {
  const { showToast } = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 20
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editForm, setEditForm] = useState({
    displayName: '',
    email: '',
    bio: '',
    verified: false,
    avatarUrl: '',
  })
  const [savingUser, setSavingUser] = useState(false)
  const [avatarUpload, setAvatarUpload] = useState<UploadedFile | null>(null)

  useEffect(() => {
    loadUsers()
  }, [filter, currentPage])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getUsers({
        filter,
        page: currentPage,
        limit: itemsPerPage
      })
      let rawData = []
      let pagination = null

      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {users: [], pagination: {}}}
        rawData = response.data?.data?.users || response.data?.users || response.data?.data || response.data?.items || []
        pagination = response.data?.data?.pagination || response.data?.pagination
      }

      // Transform snake_case to camelCase
      const data = transformUsers(Array.isArray(rawData) ? rawData : [])
      setUsers(data)

      // Update pagination info
      if (pagination) {
        setTotalPages(pagination.totalPages || Math.ceil(pagination.total / itemsPerPage) || 1)
        setTotalItems(pagination.total || rawData.length)
      } else {
        // If no pagination info, assume single page
        setTotalPages(1)
        setTotalItems(rawData.length)
      }
    } catch (error) {
      console.error('Failed to load users:', error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const handleBanUser = async (user: User) => {
    const reason = prompt('Reason for banning:')
    if (!reason) return

    try {
      await adminApi.banUser(user.id, reason)
      loadUsers()
    } catch (error) {
      console.error('Failed to ban user:', error)
    }
  }

  const handleUnbanUser = async (user: User) => {
    try {
      await adminApi.unbanUser(user.id)
      loadUsers()
    } catch (error) {
      console.error('Failed to unban user:', error)
    }
  }

  const openEditModal = (user: User) => {
    setEditingUser(user)
    setEditForm({
      displayName: user.displayName || '',
      email: user.email || '',
      bio: user.bio || '',
      verified: Boolean(user.verified),
      avatarUrl: user.avatarUrl || '',
    })
    setAvatarUpload(
      user.avatarUrl
        ? {
            id: 'existing-avatar',
            url: user.avatarUrl,
            fileType: 'image',
            size: 0,
            filename: 'Current avatar',
            originalFilename: 'Current avatar',
          }
        : null
    )
    setEditModalOpen(true)
  }

  const closeEditModal = () => {
    setEditingUser(null)
    setEditModalOpen(false)
    setEditForm({
      displayName: '',
      email: '',
      bio: '',
      verified: false,
      avatarUrl: '',
    })
    setAvatarUpload(null)
  }

  const handleEditSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!editingUser) return

    setSavingUser(true)

    try {
      await adminApi.updateUser(editingUser.id, {
        display_name: editForm.displayName.trim(),
        email: editForm.email.trim(),
        bio: editForm.bio.trim() || null,
        verified: editForm.verified,
        avatar_url: editForm.avatarUrl.trim() || null,
      })

      await loadUsers()
      closeEditModal()
    } catch (error) {
      console.error('Failed to update user:', error)
      showToast('Failed to update user. Please try again.', { type: 'error' })
    } finally {
      setSavingUser(false)
    }
  }

  return (
    <>
      <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <p className="text-gray-600 mt-1">Manage and moderate users</p>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-6">
          {['all', 'active', 'banned', 'inactive'].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === type
                  ? 'text-black hover:opacity-90'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={filter === type ? { backgroundColor: '#97DF06' } : undefined}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No users found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-gray-700">User</th>
                    <th className="pb-3 font-medium text-gray-700">Email</th>
                    <th className="pb-3 font-medium text-gray-700">Subscription</th>
                    <th className="pb-3 font-medium text-gray-700">Joined</th>
                    <th className="pb-3 font-medium text-gray-700">Status</th>
                    <th className="pb-3 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt={user.displayName}
                              className="h-10 w-10 rounded-full"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-600 font-semibold">
                                {user.displayName?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{user.displayName}</p>
                            {user.bio && (
                              <p className="text-sm text-gray-600 truncate max-w-xs">{user.bio}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-gray-700">{user.email}</td>
                      <td className="py-3">
                        <span className={`badge ${
                          user.subscriptionType === 'pro' || user.subscriptionType === 'premium' ? 'badge-success' : 'badge-info'
                        }`}>
                          {user.subscriptionType}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-700">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <span className={`badge ${
                          user.isBanned ? 'badge-danger' :
                          user.isActive ? 'badge-success' :
                          'badge-warning'
                        }`}>
                          {user.isBanned ? 'Banned' : user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-blue-50 text-blue-600 rounded-lg text-sm font-medium transition-colors"
                          >
                            Edit
                          </button>
                          {user.isBanned ? (
                            <button
                              onClick={() => handleUnbanUser(user)}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-green-50 text-green-600 rounded-lg text-sm font-medium transition-colors"
                            >
                              <CheckCircle className="h-4 w-4 inline mr-1" />
                              Unban
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBanUser(user)}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium transition-colors"
                            >
                              <Ban className="h-4 w-4 inline mr-1" />
                              Ban
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>
    </div>

      {editModalOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-900/50" onClick={closeEditModal} />

          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Update profile details for {editingUser.displayName}
                </p>
              </div>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display name
                </label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="input w-full"
                  required
                />
              </div>

              <SingleFileUploader
                label="Avatar"
                helperText="Upload a square image; JPG, PNG, or WEBP recommended."
                value={avatarUpload}
                onChange={(file) => {
                  setAvatarUpload(file)
                  setEditForm((prev) => ({
                    ...prev,
                    avatarUrl: file?.url || '',
                  }))
                }}
                userId={editingUser.id}
                accept="image/*"
                privacy="public"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, bio: e.target.value }))}
                  className="input w-full min-h-[100px]"
                  placeholder="Short description"
                />
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={editForm.verified}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, verified: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Verified user
              </label>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="btn-secondary flex-1"
                  disabled={savingUser}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={savingUser}
                >
                  {savingUser ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
